import { usePremadeQuizzes } from '@quizzach/api-hooks';
import { IcOpenPage, IcPlayOutline, LogoWordmark } from '@quizzach/assets';
import { AlertMessage } from '@quizzach/components';
import { useAuth } from '@quizzach/contexts';
import {
	ApiFailedReason,
	UserQuizzesOutletContextType,
} from '@quizzach/interfaces';
import {
	AnCard,
	AnCardGrid,
	AnEmpty,
	AnHRow,
	AnHRows,
	AnHead,
	AnStats,
	AnTabs,
	Sk,
} from '@quizzach/styles';
import { convertUnixTimestamp, copiesSoldText } from '@quizzach/utils';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useOutletContext } from 'react-router';
import { useTheme } from 'styled-components';
import {
	MOCK_AUTHOR_ANALYTICS,
	MOCK_AUTHOR_SLUG,
	useAuthorAnalytics,
} from '@quizzach/api-hooks';

export const UserAuthorAnalytics = () => {
	const { t } = useTranslation();
	const {
		profileInfo,
		loading: profileLoading,
		error: profileError,
	} = useAuth();
	const {
		authorAnalytics,
		error: authorAnalyticsError,
		loading: authorAnalyticsLoading,
		uuidOccurrences,
		getAuthorStatistics,
	} = useAuthorAnalytics();
	const {
		premadeQuizzes,
		error: premadeQuizzesError,
		loading: premadeQuizzesLoading,
		getPremadeQuizzes,
	} = usePremadeQuizzes();

	const { authoredQuizzesToList } =
		useOutletContext<UserQuizzesOutletContextType>();

	const navigate = useNavigate();
	const theme = useTheme() as { mode?: string } | undefined;
	// Brand-tinted collaborator logos in both modes, same variant pair as
	// the premade cards (AuthorMark).
	const collabVariant = theme?.mode === 'dark' ? '-branddark' : '-brand';

	const [active, setActive] = useState(1);

	const getUserQuizUuidByPremadeUuid = (
		premadeUuid: string
	): string | undefined => {
		const match = authoredQuizzesToList.find(
			(userQuiz) => userQuiz.premade_quiz?.uuid === premadeUuid
		);
		return match?.uuid;
	};

	// While mocking, the whole tab impersonates the MOCK_AUTHOR_SLUG author
	const analyticsSlug =
		MOCK_AUTHOR_ANALYTICS && MOCK_AUTHOR_SLUG
			? MOCK_AUTHOR_SLUG
			: profileInfo?.display_name_slug;

	const displayName =
		MOCK_AUTHOR_ANALYTICS && MOCK_AUTHOR_SLUG
			? premadeQuizzes?.[0]?.author?.display_name
			: profileInfo?.display_name;

	useEffect(() => {
		getAuthorStatistics(analyticsSlug);
	}, []);

	useEffect(() => {
		if (analyticsSlug) {
			getPremadeQuizzes({ author: analyticsSlug });
		}
	}, [profileInfo]);

	// While mocking, a rejected dev token must not blank the tab
	if (profileError && !MOCK_AUTHOR_ANALYTICS)
		return (
			<div style={{ padding: '30px var(--pad)' }}>
				<AlertMessage
					code={ApiFailedReason.default}
					type='error'
					style={{ margin: '0px auto' }}
				/>
			</div>
		);

	return (
		<div data-id='author-analytics-container'>
			<AnHead>
				{profileLoading ? (
					<span className='disp ghost'>.</span>
				) : (
					<>
						{displayName === 'quizzach' && (
							<span className='logo-quizzach' aria-label='quizzach'>
								<LogoWordmark width={126} height={39} />
							</span>
						)}
						{displayName === 'Nogač' && (
							<img
								src={`/collaborators/collaborator_logo-nogac${collabVariant}.png`}
								alt='Nogac'
								className='logo-nogac'
							/>
						)}
						{displayName === 'Vragolasti Tenis' && (
							<img
								src={`/collaborators/collaborator_logo-vragolasti${collabVariant}.png`}
								alt='Vragolasti Tenis'
								className='logo-vragolasti'
							/>
						)}
						{displayName === 'Putoholičari' && (
							<img
								src={`/collaborators/collaborator_logo-putoholicari${collabVariant}.png`}
								alt='Putoholičari'
								className='logo-vragolasti'
							/>
						)}
						{displayName === 'Goodtalking' && (
							<img
								src={`/collaborators/collaborator_logo-goodtalking${collabVariant}.png`}
								alt='Goodtalking'
								className='logo-vragolasti'
							/>
						)}
						{displayName !== 'quizzach' &&
							displayName !== 'Vragolasti Tenis' &&
							displayName !== 'Putoholičari' &&
							displayName !== 'Nogač' &&
							displayName !== 'Goodtalking' && (
								<span className='disp'>{displayName}</span>
							)}
					</>
				)}
				<AnStats>
					<div className='cell'>
						<span className={`n ${premadeQuizzesLoading ? 'ghost' : ''}`}>
							{premadeQuizzesLoading ? 0 : premadeQuizzes?.length}
						</span>
						<span className='l'>
							{t('profile.authorAnalytics.totalPublished')}
						</span>
					</div>
					<div className='cell'>
						<span className={`n ${premadeQuizzesLoading ? 'ghost' : ''}`}>
							{premadeQuizzesLoading ? 0 : authorAnalytics?.length}
						</span>
						<span className='l'>{t('profile.authorAnalytics.totalSold')}</span>
					</div>
				</AnStats>
			</AnHead>

			<AnTabs>
				<button
					type='button'
					className={active === 1 ? 'on' : ''}
					onClick={() => setActive(1)}
				>
					{t('profile.authorAnalytics.salesPerQuiz')}
				</button>
				<button
					type='button'
					className={active === 2 ? 'on' : ''}
					onClick={() => setActive(2)}
				>
					{t('profile.authorAnalytics.salesHistory')}
				</button>
			</AnTabs>

			{active === 1 && (
				<AnCardGrid>
					{premadeQuizzesLoading &&
						Array.from({ length: 4 }).map((_, index) => (
							<Sk key={index} className='an-card-sk' />
						))}

					{premadeQuizzesError && !premadeQuizzesLoading && (
						<div style={{ gridColumn: '1 / -1' }}>
							<AlertMessage
								code={ApiFailedReason.default}
								type='error'
								style={{ margin: '0px auto' }}
							/>
						</div>
					)}

					{!premadeQuizzesLoading &&
						premadeQuizzes?.map((quiz) => {
							const sold = uuidOccurrences.get(quiz.uuid) || 0;

							return (
								<AnCard key={quiz.uuid}>
									<div className='ph' data-darkzone>
										<img
											src={`${
												import.meta.env.VITE_ASSETS_URL
											}/thematic_quizzes/${quiz?.cover_photo_small}`}
											alt={quiz?.title ?? ''}
											loading='lazy'
										/>
										<div className='acts'>
											<button
												type='button'
												className='ic'
												aria-label={t('profile.quizCard.start')}
												title={t('profile.quizCard.start')}
												onClick={() =>
													navigate(
														`/quiz/${getUserQuizUuidByPremadeUuid(
															quiz.uuid
														)}/play`
													)
												}
											>
												<IcPlayOutline size={18} />
											</button>
											<button
												type='button'
												className='ic'
												aria-label={t('profile.authorAnalytics.viewPage')}
												title={t('profile.authorAnalytics.viewPage')}
												onClick={() => navigate(`/premade/quiz/${quiz.uuid}`)}
											>
												<IcOpenPage size={17} />
											</button>
										</div>
									</div>
									<div className='bod'>
										<h3>{quiz?.title}</h3>
										<div className='tot'>
											<span className={`n${sold === 0 ? ' zero' : ''}`}>
												{sold}
											</span>
											<span className='l'>{copiesSoldText(sold)}</span>
										</div>
									</div>
									<div className='zig' aria-hidden='true' />
								</AnCard>
							);
						})}
				</AnCardGrid>
			)}
			{active === 2 && (
				<AnHRows>
					{authorAnalyticsError && (
						<div style={{ padding: '30px var(--pad)' }}>
							<AlertMessage
								code={ApiFailedReason.default}
								type='error'
								style={{ margin: '0px auto' }}
							/>
						</div>
					)}
					{authorAnalytics &&
					!authorAnalyticsLoading &&
					authorAnalytics?.length > 0 ? (
						<>
							{authorAnalytics?.map((item) => (
								<AnHRow key={`${item.premade_quiz?.uuid}-${item.created_at}`}>
									<span className='th' data-darkzone>
										<img
											src={`${
												import.meta.env.VITE_ASSETS_URL
											}/thematic_quizzes/${item.premade_quiz
												?.cover_photo_small}`}
											alt=''
											loading='lazy'
										/>
									</span>
									<span className='when'>
										{convertUnixTimestamp(+item?.created_at)}
									</span>
									<span className='title'>{item.premade_quiz.title}</span>
								</AnHRow>
							))}
						</>
					) : (
						<AnEmpty>{t('profile.authorAnalytics.emptySales')}</AnEmpty>
					)}
				</AnHRows>
			)}
		</div>
	);
};
